/**
 * Request/response schemas for the ophthalmology specialty module (`BACKLOG.md` M10). Same reasoning
 * as `orthopedicsRequests.ts`: these live in `shared/` so the server validates against the exact schema
 * the web app infers its types from, every object schema is strict so an unrecognised key is a
 * rejection rather than silently ignored, and no server-owned value appears in any request schema.
 *
 * **Server-authoritative values never accepted from the client**, per
 * `claude-kit/rules/invariants.md`:
 *
 *   - `ophthalmologyEyewearPrescriptions.expiresOn` — computed from `issuedOn` via
 *     `defaultRxExpiration`, never trusted from the caller.
 *   - `ophthalmologyCataractSurgicalPlans.globalPeriodDays` — looked up from `cptCode` via
 *     `findOphthalmologyCataractCptCode`, matching `orthopedicsRequests.ts`'s identical precedent for
 *     `orthopedicsGlobalSurgicalPeriods.globalPeriodDays`.
 */

import { z } from 'zod';

import { calendarDateSchema, confidentialityReasonsInputSchema } from './clinicalRequests.ts';
import {
  isConsistentEyeRefractionReading,
  MAX_AXIS_DEGREES,
  MIN_AXIS_DEGREES,
  PRISM_BASES,
  REFRACTION_TYPES,
} from './ophthalmology/refraction.ts';
import { EYE_LATERALITIES, SURGICAL_EYE_LATERALITIES } from './ophthalmology/eyeLaterality.ts';
import { IMAGING_REPORT_TYPES } from './ophthalmology/imagingReports.ts';
import { IOL_FORMULAS } from './ophthalmology/iolFormulas.ts';
import { OPHTHALMOLOGY_CATARACT_CPT_CODE_VALUES } from './ophthalmology/cataractGlobalPeriod.ts';
import { RELEASE_METHODS, RX_TYPES } from './ophthalmology/eyewearRx.ts';

const trimmed = (max: number) => z.string().trim().min(1).max(max);

export const OPHTHALMOLOGY_MAX_STORAGE_REF_LENGTH = 500;
export const OPHTHALMOLOGY_MAX_KEY_METRIC_LENGTH = 200;
export const OPHTHALMOLOGY_MAX_NOTES_LENGTH = 1000;
export const OPHTHALMOLOGY_MAX_CONTACT_LENS_BRAND_LENGTH = 100;

const sphereSchema = z.number().min(-20).max(20);
const cylinderSchema = z.number().min(-10).max(10);
const axisSchema = z.number().int().min(MIN_AXIS_DEGREES).max(MAX_AXIS_DEGREES);
const addSchema = z.number().min(0).max(4);
const prismDiopterSchema = z.number().min(0).max(10);
const prismBaseSchema = z.enum(PRISM_BASES);

/** One eye's reading, as it appears flattened onto `od*`/`os*` request fields — every field
 * independently nullable (a monocular exam, e.g. one eye patched, is real), consistency between
 * cylinder and axis enforced by the caller via `isConsistentEyeRefractionReading`. */
function eyeReadingIssues(
  reading: { sphere: number | null; cylinder: number | null; axisDegrees: number | null },
  path: string,
  ctx: z.RefinementCtx,
): void {
  if (!isConsistentEyeRefractionReading({ ...reading, add: null })) {
    ctx.addIssue({
      code: 'custom',
      path: [path],
      message: 'cylinder and axis must both be present or both be absent',
    });
  }
}

// --- Refractions ---------------------------------------------------------------------------------

export const createRefractionRequestSchema = z
  .object({
    refractionType: z.enum(REFRACTION_TYPES),
    performedOn: calendarDateSchema,
    odSphere: sphereSchema.nullable().default(null),
    odCylinder: cylinderSchema.nullable().default(null),
    odAxisDegrees: axisSchema.nullable().default(null),
    odAdd: addSchema.nullable().default(null),
    odPrismDiopters: prismDiopterSchema.nullable().default(null),
    odPrismBase: prismBaseSchema.nullable().default(null),
    osSphere: sphereSchema.nullable().default(null),
    osCylinder: cylinderSchema.nullable().default(null),
    osAxisDegrees: axisSchema.nullable().default(null),
    osAdd: addSchema.nullable().default(null),
    osPrismDiopters: prismDiopterSchema.nullable().default(null),
    osPrismBase: prismBaseSchema.nullable().default(null),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    eyeReadingIssues(
      { sphere: value.odSphere, cylinder: value.odCylinder, axisDegrees: value.odAxisDegrees },
      'odAxisDegrees',
      ctx,
    );
    eyeReadingIssues(
      { sphere: value.osSphere, cylinder: value.osCylinder, axisDegrees: value.osAxisDegrees },
      'osAxisDegrees',
      ctx,
    );
    if (value.odPrismDiopters !== null && value.odPrismBase === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['odPrismBase'],
        message: 'required when odPrismDiopters is set',
      });
    }
    if (value.osPrismDiopters !== null && value.osPrismBase === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['osPrismBase'],
        message: 'required when osPrismDiopters is set',
      });
    }
  });
export type CreateRefractionRequest = z.infer<typeof createRefractionRequestSchema>;

export const refractionResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  refractionType: z.string(),
  performedOn: calendarDateSchema,
  odSphere: z.number().nullable(),
  odCylinder: z.number().nullable(),
  odAxisDegrees: z.number().nullable(),
  odAdd: z.number().nullable(),
  odPrismDiopters: z.number().nullable(),
  odPrismBase: z.string().nullable(),
  osSphere: z.number().nullable(),
  osCylinder: z.number().nullable(),
  osAxisDegrees: z.number().nullable(),
  osAdd: z.number().nullable(),
  osPrismDiopters: z.number().nullable(),
  osPrismBase: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type RefractionResponse = z.infer<typeof refractionResponseSchema>;

export const refractionEnvelopeSchema = z.object({ refraction: refractionResponseSchema });
export const refractionListEnvelopeSchema = z.object({
  refractions: z.array(refractionResponseSchema),
});

// --- Eyewear prescriptions -------------------------------------------------------------------------

export const createEyewearPrescriptionRequestSchema = z
  .object({
    rxType: z.enum(RX_TYPES),
    sourceRefractionId: z.uuid().nullable().default(null),
    odSphere: sphereSchema.nullable().default(null),
    odCylinder: cylinderSchema.nullable().default(null),
    odAxisDegrees: axisSchema.nullable().default(null),
    odAdd: addSchema.nullable().default(null),
    osSphere: sphereSchema.nullable().default(null),
    osCylinder: cylinderSchema.nullable().default(null),
    osAxisDegrees: axisSchema.nullable().default(null),
    osAdd: addSchema.nullable().default(null),
    pupillaryDistanceMm: z.number().min(40).max(85).nullable().default(null),
    contactLensBrand: trimmed(OPHTHALMOLOGY_MAX_CONTACT_LENS_BRAND_LENGTH).nullable().default(null),
    contactLensBaseCurve: z.number().min(5).max(12).nullable().default(null),
    contactLensDiameterMm: z.number().min(10).max(16).nullable().default(null),
    issuedOn: calendarDateSchema,
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    eyeReadingIssues(
      { sphere: value.odSphere, cylinder: value.odCylinder, axisDegrees: value.odAxisDegrees },
      'odAxisDegrees',
      ctx,
    );
    eyeReadingIssues(
      { sphere: value.osSphere, cylinder: value.osCylinder, axisDegrees: value.osAxisDegrees },
      'osAxisDegrees',
      ctx,
    );
    if (value.rxType === 'contact_lens' && value.contactLensBrand === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['contactLensBrand'],
        message: 'required for a contact-lens Rx',
      });
    }
  });
export type CreateEyewearPrescriptionRequest = z.infer<
  typeof createEyewearPrescriptionRequestSchema
>;

export const releaseEyewearPrescriptionRequestSchema = z
  .object({
    releaseMethod: z.enum(RELEASE_METHODS),
  })
  .strict();
export type ReleaseEyewearPrescriptionRequest = z.infer<
  typeof releaseEyewearPrescriptionRequestSchema
>;

export const eyewearPrescriptionResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  rxType: z.string(),
  sourceRefractionId: z.uuid().nullable(),
  odSphere: z.number().nullable(),
  odCylinder: z.number().nullable(),
  odAxisDegrees: z.number().nullable(),
  odAdd: z.number().nullable(),
  osSphere: z.number().nullable(),
  osCylinder: z.number().nullable(),
  osAxisDegrees: z.number().nullable(),
  osAdd: z.number().nullable(),
  pupillaryDistanceMm: z.number().nullable(),
  contactLensBrand: z.string().nullable(),
  contactLensBaseCurve: z.number().nullable(),
  contactLensDiameterMm: z.number().nullable(),
  issuedOn: calendarDateSchema,
  expiresOn: calendarDateSchema,
  releaseStatus: z.string(),
  releasedAt: z.string().nullable(),
  releaseMethod: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type EyewearPrescriptionResponse = z.infer<typeof eyewearPrescriptionResponseSchema>;

export const eyewearPrescriptionEnvelopeSchema = z.object({
  eyewearPrescription: eyewearPrescriptionResponseSchema,
});
export const eyewearPrescriptionListEnvelopeSchema = z.object({
  eyewearPrescriptions: z.array(eyewearPrescriptionResponseSchema),
});

// --- Imaging reports -------------------------------------------------------------------------------

export const createImagingReportRequestSchema = z
  .object({
    reportType: z.enum(IMAGING_REPORT_TYPES),
    eye: z.enum(EYE_LATERALITIES),
    performedOn: calendarDateSchema,
    storageRef: trimmed(OPHTHALMOLOGY_MAX_STORAGE_REF_LENGTH).nullable().default(null),
    keyMetric: trimmed(OPHTHALMOLOGY_MAX_KEY_METRIC_LENGTH).nullable().default(null),
    notes: trimmed(OPHTHALMOLOGY_MAX_NOTES_LENGTH).nullable().default(null),
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateImagingReportRequest = z.infer<typeof createImagingReportRequestSchema>;

export const imagingReportResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  reportType: z.string(),
  eye: z.string(),
  performedOn: calendarDateSchema,
  storageRef: z.string().nullable(),
  keyMetric: z.string().nullable(),
  notes: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type ImagingReportResponse = z.infer<typeof imagingReportResponseSchema>;

export const imagingReportEnvelopeSchema = z.object({ imagingReport: imagingReportResponseSchema });
export const imagingReportListEnvelopeSchema = z.object({
  imagingReports: z.array(imagingReportResponseSchema),
});

// --- Cataract surgical plans -----------------------------------------------------------------------

export const createCataractSurgicalPlanRequestSchema = z
  .object({
    eye: z.enum(SURGICAL_EYE_LATERALITIES),
    axialLengthMm: z.number().min(15).max(35),
    k1Diopters: z.number().min(30).max(60),
    k2Diopters: z.number().min(30).max(60),
    acdMm: z.number().min(1).max(6).nullable().default(null),
    iolFormula: z.enum(IOL_FORMULAS),
    priorRefractiveSurgery: z.boolean().default(false),
    targetRefraction: z.number().min(-6).max(6),
    selectedIolPowerDiopters: z.number().min(0).max(40),
    cptCode: z.enum(OPHTHALMOLOGY_CATARACT_CPT_CODE_VALUES),
    plannedSurgeryDate: calendarDateSchema,
    confidentialityReasons: confidentialityReasonsInputSchema,
  })
  .strict();
export type CreateCataractSurgicalPlanRequest = z.infer<
  typeof createCataractSurgicalPlanRequestSchema
>;

export const cataractSurgicalPlanResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  eye: z.string(),
  axialLengthMm: z.number(),
  k1Diopters: z.number(),
  k2Diopters: z.number(),
  acdMm: z.number().nullable(),
  iolFormula: z.string(),
  priorRefractiveSurgery: z.boolean(),
  targetRefraction: z.number(),
  selectedIolPowerDiopters: z.number(),
  cptCode: z.string(),
  globalPeriodDays: z.number().int(),
  plannedSurgeryDate: calendarDateSchema,
  /** Server-computed from `plannedSurgeryDate` + `globalPeriodDays` at response time, matching
   * `orthopedicsGlobalSurgicalPeriods`'s identical `windowEndDate` field — never stored, so a
   * `globalPeriodDays` correction can never leave a stale window on an old plan. */
  windowEndDate: calendarDateSchema,
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
});
export type CataractSurgicalPlanResponse = z.infer<typeof cataractSurgicalPlanResponseSchema>;

export const cataractSurgicalPlanEnvelopeSchema = z.object({
  cataractSurgicalPlan: cataractSurgicalPlanResponseSchema,
});
export const cataractSurgicalPlanListEnvelopeSchema = z.object({
  cataractSurgicalPlans: z.array(cataractSurgicalPlanResponseSchema),
});
